import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../ui/Modal';
import { PoReviewPanel } from './PoReviewPanel';
import {
  buildPoSourcePreview,
  cancelActivePoExtract,
  dragEventHasFiles,
  extractPoOrdersFromFile,
  isPoSourceFile,
  pickPoSourceFile,
  PO_SOURCE_ACCEPT,
  preparePoSourceFile,
  type PoSourcePreview,
} from '../../lib/demPoExtract';
import {
  loadPoMappingContext,
  persistPoMappings,
  prepareExtractOrder,
  type PoMappingContext,
} from '../../lib/demPoMapping';
import { checkPoDuplicate, savePoOrderToPending } from '../../lib/demPoOrders';
import { elapsedProcessingSeconds, formatProcessingDuration } from '../../lib/demProcessingTime';
import type { DemPriceRow } from '../../lib/demPoPricing';
import type { DemPoExtractHeader, DemPoReviewRow } from '../../types/dem';

type BatchEntry = {
  header: DemPoExtractHeader;
  reviewRows: DemPoReviewRow[];
  priceRows: DemPriceRow[];
  status: 'READY' | 'MISSING_CODE' | 'CHECK_ITEMS' | 'SAVED';
};

type ExtractPhase = 'email' | 'ai' | 'mapping';

type PoProcessingModalProps = {
  open: boolean;
  onClose: () => void;
  actor: string;
  initialFile?: File | null;
  onSaved: (lines: string[], tone: 'ok' | 'warn') => void;
};

function computeBatchStatus(header: DemPoExtractHeader, rows: DemPoReviewRow[]): BatchEntry['status'] {
  if (!header.sys_customer_code?.trim()) return 'MISSING_CODE';
  const unmapped = countUnmappedRows(rows);
  return unmapped > 0 ? 'CHECK_ITEMS' : 'READY';
}

function countUnmappedRows(rows: DemPoReviewRow[]): number {
  return rows.filter((row) => {
    const code = row.sysCode.trim().toUpperCase();
    if (['IGNORE', 'SKIP', 'N/A', 'NONE'].includes(code)) return false;
    return row.finalQty > 0 && !row.sysCode.trim();
  }).length;
}

function revokePreview(preview: PoSourcePreview | null) {
  if (preview?.kind === 'pdf' || preview?.kind === 'html') {
    URL.revokeObjectURL(preview.url);
  }
}

function renderPreview(preview: PoSourcePreview) {
  if (preview.kind === 'pdf') {
    return (
      <object
        data={preview.url}
        type="application/pdf"
        className="dem-po-processing__iframe"
        aria-label="PDF preview"
      >
        <p className="dem-po-processing__pdf-fallback">
          PDF preview is not available in this browser. The file is still used for AI extraction.
        </p>
      </object>
    );
  }
  if (preview.kind === 'html') {
    return (
      <iframe
        src={preview.url}
        className="dem-po-processing__iframe dem-po-processing__iframe--html"
        title={preview.label || 'Email HTML preview'}
        sandbox="allow-same-origin"
      />
    );
  }
  if (preview.kind === 'excel') {
    return (
      <div className="dem-po-processing__excel-wrap">
        <table className="dem-po-processing__excel-table">
          <tbody>
            {preview.rows.map((row, rowIndex) => (
              <tr key={`${preview.fileName}-${rowIndex}`}>
                {row.map((cell, cellIndex) => (
                  <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (preview.kind === 'email') {
    return (
      <div className="dem-po-processing__email-preview">
        <div className="dem-po-processing__email-preview-banner">Email source</div>
        {preview.subject ? (
          <h4 className="dem-po-processing__email-preview-subject">{preview.subject}</h4>
        ) : null}
        {preview.meta.length > 0 ? (
          <dl className="dem-po-processing__email-preview-meta">
            {preview.meta.map((item) => (
              <div key={`${item.label}-${item.value}`} className="dem-po-processing__email-preview-meta-row">
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        <div className="dem-po-processing__email-preview-body">{preview.body}</div>
      </div>
    );
  }
  return <pre className="dem-po-processing__text-preview">{preview.text}</pre>;
}

export function PoProcessingModal({ open, onClose, actor, initialFile, onSaved }: PoProcessingModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PoSourcePreview | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractPhase, setExtractPhase] = useState<ExtractPhase | null>(null);
  const [extractElapsedSec, setExtractElapsedSec] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappingCtx, setMappingCtx] = useState<PoMappingContext | null>(null);
  const mappingCtxRef = useRef<PoMappingContext | null>(null);
  const [batch, setBatch] = useState<BatchEntry[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [dropActive, setDropActive] = useState(false);
  const dropDepthRef = useRef(0);
  const initialHandledRef = useRef(false);
  const [extractAttempted, setExtractAttempted] = useState(false);
  const [duplicateNotice, setDuplicateNotice] = useState<{ index: number; message: string } | null>(null);
  const duplicateAckRef = useRef<Set<number>>(new Set());
  /** Wall-clock session start (ingest / drop). Cleared on close without save. */
  const sessionStartedAtRef = useRef<number | null>(null);
  /** Only the first successful save in this session gets processing_seconds. */
  const processingSecondsAppliedRef = useRef(false);
  const [sessionElapsedSec, setSessionElapsedSec] = useState(0);

  const activeEntry = activeIndex != null ? batch[activeIndex] : null;

  const waitForMappingContext = async (): Promise<PoMappingContext> => {
    for (let attempt = 0; attempt < 600; attempt += 1) {
      const ctx = mappingCtxRef.current;
      if (ctx) return ctx;
      await new Promise((resolve) => window.setTimeout(resolve, 50));
    }
    throw new Error('Mapping data is still loading. Please try again in a moment.');
  };

  const resetState = () => {
    revokePreview(preview);
    setFile(null);
    setPreview(null);
    setExtracting(false);
    setExtractPhase(null);
    setExtractElapsedSec(0);
    setSaving(false);
    setError(null);
    setMappingCtx(null);
    setBatch([]);
    setActiveIndex(null);
    initialHandledRef.current = false;
    setExtractAttempted(false);
    setDuplicateNotice(null);
    duplicateAckRef.current = new Set();
    sessionStartedAtRef.current = null;
    processingSecondsAppliedRef.current = false;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    mappingCtxRef.current = mappingCtx;
  }, [mappingCtx]);

  useEffect(() => {
    if (!extracting) {
      setExtractElapsedSec(0);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(() => {
      setExtractElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [extracting]);

  useEffect(() => {
    if (!open) {
      setSessionElapsedSec(0);
      return;
    }
    const tick = () => {
      setSessionElapsedSec(elapsedProcessingSeconds(sessionStartedAtRef.current));
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [open, file]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    void loadPoMappingContext()
      .then(setMappingCtx)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load mapping data.'));
  }, [open]);

  useEffect(() => {
    return () => {
      revokePreview(preview);
    };
  }, [preview]);

  const ingestFile = async (rawFile: File) => {
    setError(null);
    setBatch([]);
    setActiveIndex(null);
    setExtractAttempted(false);
    setDuplicateNotice(null);
    duplicateAckRef.current = new Set();
    processingSecondsAppliedRef.current = false;
    sessionStartedAtRef.current = Date.now();
    revokePreview(preview);
    setPreview(null);

    if (!isPoSourceFile(rawFile)) {
      setError('Supported: PDF, Excel, CSV, EML, MSG.');
      setFile(null);
      return;
    }

    setExtracting(true);
    const isEmail = ['eml', 'msg'].includes(rawFile.name.split('.').pop()?.toLowerCase() ?? '');
    if (isEmail) setExtractPhase('email');
    try {
      const prepared = await preparePoSourceFile(rawFile);
      const processingFile = prepared.file;
      const nextPreview = prepared.preview ?? (await buildPoSourcePreview(processingFile));
      setFile(processingFile);
      setPreview(nextPreview);
      if (!mappingCtxRef.current) {
        setExtractPhase('mapping');
      }
      await waitForMappingContext();
      await runExtraction(processingFile);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to prepare source file.');
      setFile(null);
      setPreview(null);
      setExtracting(false);
      setExtractPhase(null);
    }
  };

  useEffect(() => {
    if (!open || !initialFile || initialHandledRef.current) return;
    initialHandledRef.current = true;
    void ingestFile(initialFile);
  }, [open, initialFile]);

  const handleClose = () => {
    if (extracting || saving) return;
    onClose();
  };

  const handleFileChange = async (next: File | null) => {
    if (!next) {
      setFile(null);
      revokePreview(preview);
      setPreview(null);
      return;
    }
    await ingestFile(next);
  };

  const handleCancelExtract = () => {
    cancelActivePoExtract();
    setExtracting(false);
    setExtractPhase(null);
    setError('Extraction cancelled.');
  };

  const runExtraction = async (sourceFile: File) => {
    const ctx = mappingCtxRef.current ?? (await waitForMappingContext());
    mappingCtxRef.current = ctx;

    setExtracting(true);
    setExtractPhase('ai');
    setError(null);
    setBatch([]);
    setActiveIndex(null);
    setExtractAttempted(true);

    try {
      const orders = await extractPoOrdersFromFile(sourceFile);
      if (orders.length === 0) {
        setError('No valid order data was extracted from this document.');
        return;
      }

      setExtractPhase('mapping');
      const entries: BatchEntry[] = [];
      for (const order of orders) {
        const { header, reviewRows, priceRows } = await prepareExtractOrder(ctx, order);
        entries.push({
          header,
          reviewRows,
          priceRows,
          status: computeBatchStatus(header, reviewRows),
        });
      }

      setBatch(entries);
      if (entries.length === 1) {
        const dup = await checkPoDuplicate(entries[0]!.header.po_number);
        if (dup.duplicate) {
          setDuplicateNotice({ index: 0, message: dup.message });
          return;
        }
        setActiveIndex(0);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'AI extraction failed.');
    } finally {
      setExtracting(false);
      setExtractPhase(null);
    }
  };

  const openBatchReview = async (index: number) => {
    const entry = batch[index];
    if (!entry || entry.status === 'SAVED') return;

    if (!duplicateAckRef.current.has(index)) {
      const dup = await checkPoDuplicate(entry.header.po_number);
      if (dup.duplicate) {
        const ok = window.confirm(
          `PO "${entry.header.po_number}" may already exist.\n${dup.message}\n\nProcess anyway?`,
        );
        if (!ok) {
          setDuplicateNotice({ index, message: dup.message });
          return;
        }
        duplicateAckRef.current.add(index);
      }
    }

    setDuplicateNotice(null);
    setActiveIndex(index);
  };

  const handleProcessDuplicateAnyway = (index: number) => {
    duplicateAckRef.current.add(index);
    setDuplicateNotice(null);
    setActiveIndex(index);
  };

  const updateActiveHeader = (header: DemPoExtractHeader) => {
    if (activeIndex == null) return;
    setBatch((prev) =>
      prev.map((entry, idx) =>
        idx === activeIndex
          ? { ...entry, header, status: computeBatchStatus(header, entry.reviewRows) }
          : entry,
      ),
    );
  };

  const updateActiveRows = (reviewRows: DemPoReviewRow[]) => {
    if (activeIndex == null) return;
    setBatch((prev) =>
      prev.map((entry, idx) =>
        idx === activeIndex
          ? { ...entry, reviewRows, status: computeBatchStatus(entry.header, reviewRows) }
          : entry,
      ),
    );
  };

  const updateActivePriceRows = (priceRows: DemPriceRow[]) => {
    if (activeIndex == null) return;
    setBatch((prev) =>
      prev.map((entry, idx) => (idx === activeIndex ? { ...entry, priceRows } : entry)),
    );
  };

  const takeSessionProcessingSeconds = (): number | null => {
    if (processingSecondsAppliedRef.current) return null;
    const sec = elapsedProcessingSeconds(sessionStartedAtRef.current);
    return sec > 0 ? sec : null;
  };

  const markProcessingSecondsApplied = () => {
    processingSecondsAppliedRef.current = true;
  };

  const handleConfirmSave = async () => {
    if (!mappingCtx || activeIndex == null || !activeEntry) return;

    setSaving(true);
    setError(null);

    try {
      await persistPoMappings(mappingCtx, activeEntry.header, activeEntry.reviewRows);
      const processingSeconds = takeSessionProcessingSeconds();
      const result = await savePoOrderToPending(
        activeEntry.header,
        activeEntry.reviewRows,
        actor,
        processingSeconds,
      );
      if (processingSeconds != null) markProcessingSecondsApplied();

      const lines = [`PO order ${result.order_id} saved to Pending.`];
      if (result.notifications.sent > 0) {
        lines.push(`Pending confirmation emails sent: ${result.notifications.sent}.`);
      }
      if (result.notifications.skippedNoEmail > 0) {
        lines.push(`${result.notifications.skippedNoEmail} skipped (no customer email).`);
      }
      if (result.notifications.failed.length > 0) {
        lines.push(
          `Email failed: ${result.notifications.failed.map((f) => f.reason).join('; ')}`,
        );
      }

      const tone: 'ok' | 'warn' =
        result.notifications.failed.length > 0 || result.notifications.skippedNoEmail > 0 ? 'warn' : 'ok';

      setBatch((prev) => {
        const next = prev.map((entry, idx) =>
          idx === activeIndex ? { ...entry, status: 'SAVED' as const } : entry,
        );
        const allSaved = next.every((e) => e.status === 'SAVED');
        if (allSaved) {
          onSaved(lines, tone);
          onClose();
        } else {
          onSaved([...lines, 'Continue reviewing remaining outlets in this document.'], tone);
        }
        return next;
      });
      setActiveIndex(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save PO order.');
    } finally {
      setSaving(false);
    }
  };

  const handleBatchSaveAll = async () => {
    if (!mappingCtx || batch.length <= 1) return;

    const missingCode = batch.filter((entry) => entry.status === 'MISSING_CODE');
    if (missingCode.length > 0) {
      setError(
        `Customer code missing for ${missingCode.map((entry) => entry.header.company_name || 'Unknown').join(', ')}. Open each outlet to fix.`,
      );
      return;
    }

    const pending = batch.filter((entry) => entry.status !== 'SAVED');
    const notReady = pending.filter((entry) => entry.status !== 'READY');
    if (notReady.length > 0) {
      const ok = window.confirm(
        `${notReady.length} outlet(s) still have unmapped items. Save all ${pending.length} anyway?`,
      );
      if (!ok) return;
    }

    setSaving(true);
    setError(null);

    const savedLines: string[] = [];
    let warnTone = false;

    try {
      for (let index = 0; index < batch.length; index += 1) {
        const entry = batch[index]!;
        if (entry.status === 'SAVED') continue;

        await persistPoMappings(mappingCtx, entry.header, entry.reviewRows);
        const processingSeconds = takeSessionProcessingSeconds();
        const result = await savePoOrderToPending(
          entry.header,
          entry.reviewRows,
          actor,
          processingSeconds,
        );
        if (processingSeconds != null) markProcessingSecondsApplied();
        savedLines.push(`PO ${result.order_id} (${entry.header.company_name || entry.header.po_number})`);
        if (result.notifications.failed.length > 0 || result.notifications.skippedNoEmail > 0) {
          warnTone = true;
        }

        setBatch((prev) =>
          prev.map((row, idx) => (idx === index ? { ...row, status: 'SAVED' as const } : row)),
        );
      }

      onSaved(
        [`Saved ${savedLines.length} outlet order(s) to Pending.`, ...savedLines],
        warnTone ? 'warn' : 'ok',
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Batch save failed.');
    } finally {
      setSaving(false);
    }
  };

  const batchSummary = useMemo(() => {
    if (batch.length === 0) return null;
    const saved = batch.filter((b) => b.status === 'SAVED').length;
    const outlets = new Set(
      batch.map((b) => (b.header.company_name || '').trim().toLowerCase()).filter(Boolean),
    ).size;
    const label =
      outlets <= 1 && batch.length > 1
        ? `${batch.length} PO(s) · ${outlets || 1} outlet`
        : `${batch.length} order(s)${outlets > 1 ? ` · ${outlets} outlets` : ''}`;
    return `${label}${saved > 0 ? ` · ${saved} saved` : ''}`;
  }, [batch]);

  const extractLoadingMessage =
    extractPhase === 'email'
      ? 'Reading email and attachments…'
      : extractPhase === 'mapping' && !mappingCtx
        ? 'Loading SKU mappings…'
        : extractPhase === 'mapping'
          ? 'Applying SKU mappings and prices…'
          : extractElapsedSec >= 45
            ? `Still extracting… (${extractElapsedSec}s). Large or complex PDFs can take up to 3 minutes.`
            : `Reading document… (${extractElapsedSec}s)`;

  const isReviewMode = Boolean(file && preview);

  const handleModalDragEnter = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current += 1;
    setDropActive(true);
  };

  const handleModalDragLeave = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) setDropActive(false);
  };

  const handleModalDragOver = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleModalDrop = (e: React.DragEvent) => {
    if (!dragEventHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.stopPropagation();
    dropDepthRef.current = 0;
    setDropActive(false);
    const next = pickPoSourceFile(e.dataTransfer.files);
    if (next) void ingestFile(next);
  };

  const batchTitle = useMemo(() => {
    if (batch.length <= 1) return 'Review extracted order';
    const outlets = new Set(
      batch.map((b) => (b.header.company_name || '').trim().toLowerCase()).filter(Boolean),
    ).size;
    if (outlets <= 1) return `Multiple POs detected (${batch.length}) · same outlet`;
    return `Multiple orders detected (${batch.length})`;
  }, [batch]);

  const renderBatchPicker = () => (
    <div className="dem-po-batch">
      {duplicateNotice ? (
        <div className="dem-po-duplicate alert-warn" role="status">
          <p className="dem-po-duplicate__title">Duplicate PO detected</p>
          <p className="dem-po-duplicate__message">
            PO <strong>{batch[duplicateNotice.index]?.header.po_number || '—'}</strong> may already exist.{' '}
            {duplicateNotice.message}
          </p>
          <div className="dem-po-duplicate__actions">
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => handleProcessDuplicateAnyway(duplicateNotice.index)}
            >
              Yes, process anyway
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setDuplicateNotice(null)}>
              Not now
            </button>
          </div>
        </div>
      ) : null}

      <h3>{batchTitle}</h3>
      {batch.length > 1 ? (
        <p className="dem-po-batch__tip">
          Consolidation lists (e.g. RE&amp;S) are split <strong>one Pending order per PO</strong>{' '}
          (same outlet can appear more than once). Review any row, or use{' '}
          <strong>Save all to Pending</strong> for the whole list.
        </p>
      ) : (
        <p className="dem-po-batch__tip">Select the order below to review items and mapping.</p>
      )}
      <div className="dem-po-batch__table-wrap">
        <div className="dem-po-batch__head" aria-hidden>
          <span>Outlet</span>
          <span>PO no.</span>
          <span>Code</span>
          <span>Items</span>
          <span>Unmapped</span>
          <span>Status</span>
        </div>
        {batch.map((entry, index) => (
          <button
            key={`${entry.header.company_name}-${entry.header.po_number}-${index}`}
            type="button"
            className={`dem-po-batch__row dem-po-batch__row--${entry.status.toLowerCase()}`}
            disabled={entry.status === 'SAVED' || saving}
            onClick={() => void openBatchReview(index)}
          >
            <span>{entry.header.company_name || 'Unknown'}</span>
            <span>{entry.header.po_number || '—'}</span>
            <span>{entry.header.sys_customer_code || '—'}</span>
            <span>{entry.reviewRows.filter((row) => row.isPrimary).length}</span>
            <span>{countUnmappedRows(entry.reviewRows)}</span>
            <span>{entry.status === 'SAVED' ? 'Saved' : entry.status.replace('_', ' ')}</span>
          </button>
        ))}
      </div>
      {batch.length > 1 ? (
        <div className="dem-po-batch__actions">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={saving || batch.every((entry) => entry.status === 'SAVED')}
            onClick={() => void handleBatchSaveAll()}
          >
            {saving ? 'Saving…' : 'Save all to Pending'}
          </button>
        </div>
      ) : null}
    </div>
  );

  const toolbar = (
    <div className="dem-po-processing__toolbar dem-po-processing__toolbar--header">
      <input
        ref={fileInputRef}
        type="file"
        accept={PO_SOURCE_ACCEPT}
        className="dem-po-processing__file-input"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => void handleFileChange(e.target.files?.[0] ?? null)}
      />
      <button
        type="button"
        className={`dem-po-processing__toolbtn${file ? '' : ' dem-po-processing__toolbtn--primary'}`}
        disabled={extracting || saving}
        onClick={() => fileInputRef.current?.click()}
      >
        {file ? 'Change file' : 'Upload PDF / Excel / Email'}
      </button>
      {file ? (
        <>
          <button
            type="button"
            className="dem-po-processing__toolbtn"
            disabled={extracting || saving}
            onClick={() => void runExtraction(file)}
            title="Re-run AI extraction"
          >
            {extracting ? 'Extracting…' : 'Re-extract'}
          </button>
          {extracting ? (
            <button
              type="button"
              className="dem-po-processing__toolbtn dem-po-processing__toolbtn--cancel"
              onClick={handleCancelExtract}
            >
              Cancel
            </button>
          ) : null}
        </>
      ) : null}
      {batchSummary ? <span className="dem-po-processing__badge">{batchSummary}</span> : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New PO"
      fullScreen={isReviewMode}
      compactHeader
      titleExtra={toolbar}
      headerActions={
        <span
          className="dem-po-processing__timer"
          title="Time spent on this PO session"
          aria-live="polite"
        >
          {sessionElapsedSec > 0 ? formatProcessingDuration(sessionElapsedSec) : '0s'}
        </span>
      }
    >
      <div
        className={`dem-po-processing${isReviewMode ? '' : ' dem-po-processing--upload'}${dropActive ? ' dem-po-processing--drop-active' : ''}`}
        onDragEnter={handleModalDragEnter}
        onDragLeave={handleModalDragLeave}
        onDragOver={handleModalDragOver}
        onDrop={handleModalDrop}
      >
        {error ? <p className="alert alert-error dem-po-processing__error">{error}</p> : null}

        {file && preview ? (
          <div className="dem-po-processing__split">
            <div className="dem-po-processing__preview">{renderPreview(preview)}</div>

            <div className="dem-po-processing__workspace">
              {extracting ? (
                <p className="dem-po-processing__loading">{extractLoadingMessage}</p>
              ) : activeEntry && mappingCtx ? (
                <PoReviewPanel
                  header={activeEntry.header}
                  rows={activeEntry.reviewRows}
                  priceRows={activeEntry.priceRows}
                  customers={mappingCtx.customers}
                  itemOptions={mappingCtx.itemOptions}
                  mappingCtx={mappingCtx}
                  saving={saving}
                  onHeaderChange={updateActiveHeader}
                  onRowsChange={updateActiveRows}
                  onPriceRowsChange={updateActivePriceRows}
                  onConfirm={() => void handleConfirmSave()}
                />
              ) : batch.length >= 1 && mappingCtx ? (
                renderBatchPicker()
              ) : batch.length === 0 && !extracting ? (
                <p className="dem-po-processing__loading">
                  {extractAttempted
                    ? 'Extraction finished with no review data. Select Re-extract to try again.'
                    : 'Preparing extraction…'}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="dem-po-processing__empty">
            Upload a customer PO as PDF, Excel (.xlsx/.xls/.csv), or forwarded email (.eml/.msg), or drag and drop a
            file here. You can also open the Email queue from the PO Orders page to process inbox orders.
          </p>
        )}
      </div>
    </Modal>
  );
}
